import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PillPosition } from 'shared/components/Card/Card';
import { CardDescription, CardDescriptionBold } from 'shared/components/Card/Card.styles';
import StatusPill from 'shared/components/StatusPill/StatusPill';
import truncateDisplayString from 'shared/utils/truncateDisplayString';
import { AutoPayCard, CardBody } from '../SystemDetails.styles';
import { MilestoneType } from '../../../../types';

export interface AutoPayStatusCardProps {
  currentMilestone: MilestoneType;
  achElected: boolean;
  achDetails: {
    verified: boolean;
    bankName?: string;
    accountLastFourDigits?: string;
  };
  onClick: () => void;
}

export function AutoPayStatusCard({
  currentMilestone,
  achElected,
  achDetails,
  onClick,
}: AutoPayStatusCardProps) {
  const { t } = useTranslation('translation', { keyPrefix: 'status.autoPay' });

  const { verified: achVerified, bankName, accountLastFourDigits } = achDetails;

  const bodyContent = useMemo(() => {
    if (achVerified) {
      return (
        <>
          <CardDescription as="body">{t('bank')}</CardDescription>
          <CardDescriptionBold as="body" $margin="0 0 8px">
            {bankName ? truncateDisplayString(bankName, 27) : t('yourBank')}
          </CardDescriptionBold>
          <CardDescription as="body">{t('account')}</CardDescription>
          <CardDescriptionBold as="body">•••• {accountLastFourDigits}</CardDescriptionBold>
        </>
      );
    }

    return achElected ? (
      <CardDescription as="body">{t('unableToValidate')}</CardDescription>
    ) : (
      <CardDescription as="body">{t('neverTooLate')}</CardDescription>
    );
  }, [achElected, achVerified, bankName, accountLastFourDigits, t]);

  const actionText = useMemo(() => {
    if (achVerified) {
      return currentMilestone !== MilestoneType.IN_SERVICE ? t('updateBankInfo') : undefined;
    }
    return t(achElected ? 'reEnter' : 'getStarted');
  }, [achVerified, t, achElected, currentMilestone]);

  return (
    <AutoPayCard
      title={t('autoPay')}
      statusPill={
        <StatusPill
          text={t(achVerified ? 'enrolled' : 'notEnrolled')}
          bgColor={achVerified ? undefined : 'backgroundSystemWarnFunction220'}
          margin={achVerified ? '0 0 0 auto' : '0 0 12px 0'}
        />
      }
      pillPosition={achVerified ? PillPosition.BESIDE : PillPosition.BELOW}
      actionText={actionText}
      onClick={onClick}
      $isAlreadyEnrolled={achVerified && currentMilestone === MilestoneType.IN_SERVICE}
    >
      <CardBody>{bodyContent}</CardBody>
    </AutoPayCard>
  );
}
